import moment from 'moment';

import {
  CUSTOM_PERIOD,
  LAST_MONTH,
  LAST_N_DAYS,
  LAST_THREE_MONTHS,
  LAST_WEEK,
  NEXT_MONTH,
  NEXT_N_DAYS,
  NEXT_THREE_MONTHS,
  NEXT_WEEK,
  ONE_DAY,
  ONE_MONTH_IN_DAYS,
  ONE_WEEK_IN_DAYS,
  THREE_MONTHS_IN_DAYS,
  TODAY,
  TOMORROW,
  YESTERDAY,
  ZERO_DAYS,
} from '@pages/Clients/ClientsList/constants/periods';

import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';

/**
 * Алгоритм
 * 1. Пользователь кликает на дату.
 * 2. Алгоритм принимает 2 выбранные даты на вход и сегодняшнюю дату.
 * 3. Алгоритм определяет сегмент выбранной даты.
 * 4. ЕСЛИ даты попадают в один из сегментов:
 * 4.1. ТО алгоритм возвращает определенный сегмент;
 * 4.2. ИНАЧЕ алгоритм снимает рендер с последнего выбранного сегмента.
 */

export const defineFastDatesFilter: NClientsList.DefineFastDatesFilter = ({
  startDate,
  endDate,
  today,
  // Этот признак определяет, вести ли подсчет дат вперед или назад
  // по шкале времени
  isShiftToFeature = false,
}) => {
  const startDateWithoutTime = moment(startDate).format('YYYY-MM-DD');
  const endDateWithoutTime = moment(endDate).format('YYYY-MM-DD');
  const todayWithoutTime = moment(today).format('YYYY-MM-DD');

  const isToday = isSegmentRange(
    defineStartDate(todayWithoutTime, ZERO_DAYS, isShiftToFeature),
    startDateWithoutTime,
    endDateWithoutTime
  );
  const isOneDay = isSegmentRange(
    defineStartDate(todayWithoutTime, ONE_DAY, isShiftToFeature),
    startDateWithoutTime,
    endDateWithoutTime,
    null,
    isShiftToFeature
  );
  const isOneWeek = isSegmentRange(
    defineStartDate(todayWithoutTime, ONE_WEEK_IN_DAYS, isShiftToFeature),
    startDateWithoutTime,
    endDateWithoutTime,
    todayWithoutTime,
    isShiftToFeature
  );
  const isOneMonth = isSegmentRange(
    defineStartDate(todayWithoutTime, ONE_MONTH_IN_DAYS, isShiftToFeature),
    startDateWithoutTime,
    endDateWithoutTime,
    todayWithoutTime,
    isShiftToFeature
  );
  const isThreeMonths = isSegmentRange(
    defineStartDate(todayWithoutTime, THREE_MONTHS_IN_DAYS, isShiftToFeature),
    startDateWithoutTime,
    endDateWithoutTime,
    todayWithoutTime,
    isShiftToFeature
  );
  const isNotSegment = startDate === undefined || endDate === undefined;
  const areNDays =
    (isShiftToFeature
      ? startDateWithoutTime === todayWithoutTime
      : endDateWithoutTime === todayWithoutTime) &&
    !isToday &&
    !isOneDay &&
    !isOneWeek &&
    !isOneMonth &&
    !isThreeMonths
      ? true
      : false;

  // TODO: если смещение дат идет вперед,
  // то создать список новых условий
  if (isNotSegment) return null;
  if (isToday) return TODAY;
  if (isOneDay) return isShiftToFeature ? TOMORROW : YESTERDAY;
  if (isOneWeek) return isShiftToFeature ? NEXT_WEEK : LAST_WEEK;
  if (isOneMonth) return isShiftToFeature ? NEXT_MONTH : LAST_MONTH;
  if (isThreeMonths)
    return isShiftToFeature ? NEXT_THREE_MONTHS : LAST_THREE_MONTHS;
  if (areNDays) return isShiftToFeature ? NEXT_N_DAYS : LAST_N_DAYS;

  return CUSTOM_PERIOD;
};

function isSegmentRange(
  dateAsString: string,
  startDate: string,
  endDate: string,
  today?: string | null,
  isShiftToFeature?: boolean
): boolean {
  const handledStartDate = isShiftToFeature ? endDate : startDate;
  const handledEndDate = isShiftToFeature ? startDate : endDate;

  return (
    dateAsString === handledStartDate &&
    (today || dateAsString) === handledEndDate
  );
}

function defineStartDate(
  today: string,
  shiftInDays: number,
  areFeatureDates?: boolean
): string {
  const todayAsDate = new Date(today);
  todayAsDate.setDate(
    areFeatureDates
      ? todayAsDate.getDate() + shiftInDays
      : todayAsDate.getDate() - shiftInDays
  );
  return todayAsDate.toISOString().split('T')[0];
}
